<script setup lang="ts">
  interface Props {
    isOpen: boolean;
    inputUrl: string;
    title?: string;
    placeholder?: string;
    type?: "video" | "url";
    isTest?: boolean; // テスト時のみ true にする。でないと v-dialog を find できないため
  }
  const props = withDefaults(defineProps<Props>(), {
    isOpen: false,
    inputUrl: "",
    title: "URLを入力してください",
    placeholder: "https://...",
    type: "url",
  });

  interface Emits {
    (event: "clickOk"): void;
    (event: "update:isOpen", isOpen: boolean): void;
    (event: "update:inputUrl", inputUrl: string): void;
  }

  const emit = defineEmits<Emits>();

  // iframeタグが入っているHTML文字列から src を取得する
  const getSrcFromIframe = (htmlString: string) => {
    // iframeタグが入っている文字列であれば src を取得する
    // DOMParser を使用して文字列を DOM に変換
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    // iframe 要素を取得
    const iframe = doc.querySelector("iframe");

    if (iframe) {
      // iframe の src を取得
      const iframeSrc = iframe.getAttribute("src");
      return iframeSrc;
    } else {
      return "";
    }
  };

  // youtubeのURLを埋め込みURLに変換する
  const getYouTubeEmbedUrl = (url: string) => {
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/;
    const match = url.match(regex);
    if (match && match[1]) {
      const videoId = match[1];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return null; // 無効なURLの場合
  };

  // VimeoのURLを埋め込みURLに変換する
  const getVimeoEmbedUrl = (url: string) => {
    // VimeoのURLから動画IDを抽出する正規表現
    const regex =
      /(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/|album\/\d+\/video\/|)|player\.vimeo\.com\/video\/)(\d+)/;
    const match = url.match(regex);
    if (match && match[1]) {
      const videoId = match[1];
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return null; // 無効なURLの場合
  };

  const clickOk = (event: Event) => {
    event.stopPropagation();
    emit("clickOk");
    computedIsOpen.value = false;
  };
  const computedIsOpen = computed<boolean>({
    get: () => props.isOpen,
    set: value => {
      emit("update:isOpen", value);
    },
  });

  const computedInputUrl = computed<string>({
    get: () => props.inputUrl ?? "",
    set: value => {
      if (props.type === "video") {
        // valueのドメインがfincs.jp or localhostかつ、 valueの最後が/embedではない場合は /embed を追加する
        if (value.includes("fincs.jp") || value.includes("localhost")) {
          if (!value.endsWith("/embed")) {
            value += "/embed";
          }
        } else if (value.includes("iframe")) {
          // iframeタグが入っている文字列であれば src を取得する
          const src = getSrcFromIframe(value);
          if (src) {
            value = src;
          }
        } else if (value.includes("youtube.com")) {
          // youtubeのURLの場合は埋め込みURLに変換する
          const embedUrl = getYouTubeEmbedUrl(value);
          if (embedUrl) {
            value = embedUrl;
          }
        } else if (value.includes("vimeo.com")) {
          // vimeoのURLの場合は埋め込みURLに変換する
          const embedUrl = getVimeoEmbedUrl(value);
          if (embedUrl) {
            value = embedUrl;
          }
        }
      }
      emit("update:inputUrl", value);
    },
  });
</script>

<template>
  <v-dialog v-model="computedIsOpen" scrim="black" :attach="!!isTest">
    <v-sheet class="dialog py-8 px-10 rounded" color="monochrome-04" align="center">
      <div class="text-dialog-title">{{ title }}</div>

      <div class="mt-6">
        <AtomsInputText v-model:text="computedInputUrl" :placeholder="placeholder" data-testid="url-input" />
      </div>

      <AtomsDialogButton @click="clickOk"> 決定 </AtomsDialogButton>
    </v-sheet>
  </v-dialog>
</template>

<style lang="scss" scoped>
  .dialog {
    min-height: 146px;
    min-width: 345px;
    width: 60%;
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    -webkit-transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
  }
</style>
